import { useCallback, useEffect, useRef, useState, Dispatch, SetStateAction } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { Browser, Document } from 'blaze-ui/icons'
import { Brand, Plus } from 'blaze-ui/icons/20'
import { Pdf, Video } from 'blaze-ui/icons/24'
import { Voice } from 'blaze-ui/icons/16'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { AddWebpageButton } from 'screens/Campaign/CampaignContext/AddWebpageModal/AddWebpageButton'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useModals } from 'almanac-ui/Modal'
import { AlmanacFile, Source, SourceMaterialsMediaObject, Website } from 'types'
import { Button } from 'blaze-ui/Button'
import { CampaignSource, CampaignSourceObject, SourceRole } from './Types'
import { SourceCard } from './SourceCard'
import { BrandKitContextModal } from './BrandKitContextModal/BrandKitContextModal'
import Styles from './AddContext.module.scss'
import { ACCEPT_WITH_MEDIA, DEFAULT_ACCEPT, UploadButton } from './UploadButton'
import { useDropzone } from 'react-dropzone'
import { pluralize } from 'utils'
import { useCreateMediaObjectsWithProgress } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useCreateSource } from 'hooks/queries/workspaces/useCreateSource'
import cx from 'classnames'

interface AddContextProps {
  sources: CampaignSource[]
  setSources: Dispatch<SetStateAction<CampaignSource[]>>
  isUploading: boolean
  setIsUploading: Dispatch<SetStateAction<boolean>>
  acceptMedia?: boolean
  onAnalysisPendingChange?: (isPending: boolean) => void
  brandKitId?: number
  brandKitMediaEnabled?: boolean
}

export const SourceIcon = ({ source }: { source: Source }) => {
  if (source.type === 'Website') {
    const website = source as Website
    if (website.favicon) {
      return <img alt="" src={website.favicon} className={Styles.sourceIconImg} />
    }
    return <Browser size={18} color="var(--dark-40)" />
  }

  if (source.type === 'SourceMaterialsMediaObject') {
    const media = source as SourceMaterialsMediaObject
    const mediaType = media.mime_type?.split('/')[0]
    const docType = media.mime_type?.split('/')[1] || media.mime_type?.split('.')[1]

    if (mediaType === 'image') {
      return <img alt="" src={media.source_url} className={Styles.sourceIconImg} />
    }
    if (mediaType === 'audio') return <Voice size={18} />
    if (mediaType === 'video') return <Video size={18} />
    if (docType === 'pdf') return <Pdf color="var(--red-50)" size={18} />
    return <Document size={18} />
  }

  return <Document size={18} />
}

export const getSourceLabel = (source: Source): { name: string; type: string } => {
  if (source.type === 'Website') {
    const website = source as Website
    return { name: website.title || website.url, type: 'Website' }
  }
  if (source.type === 'SourceMaterialsMediaObject') {
    const media = source as SourceMaterialsMediaObject
    const mediaType = media.mime_type?.split('/')[0]
    const docType = media.mime_type?.split('/')[1] || media.mime_type?.split('.')[1]

    let typeLabel = 'Document'
    if (mediaType === 'audio') typeLabel = 'Audio'
    else if (mediaType === 'video') typeLabel = 'Video'
    else if (mediaType === 'image') typeLabel = 'Image'
    else if (docType === 'pdf') typeLabel = 'PDF file'

    const parts = media.source_url?.split('/') ?? []
    const name = parts[parts.length - 1] || media.source_url
    return { name, type: typeLabel }
  }
  return { name: 'Unknown', type: 'File' }
}

const isImageAsset = (source: Source): boolean => {
  if (source.type !== 'SourceMaterialsMediaObject') return false
  const media = source as SourceMaterialsMediaObject
  return media.mime_type?.split('/')[0] === 'image'
}

const isVideoAsset = (source: Source): boolean => {
  if (source.type !== 'SourceMaterialsMediaObject') return false
  const media = source as SourceMaterialsMediaObject
  return media.mime_type?.split('/')[0] === 'video'
}

export const isVisualAsset = (source: Source): boolean => {
  if (source.type !== 'SourceMaterialsMediaObject') return false
  const media = source as SourceMaterialsMediaObject
  const mediaType = media.mime_type?.split('/')[0]
  return mediaType === 'image' || mediaType === 'video'
}

const defaultRolesForSource = (source: Source): SourceRole[] => {
  return isImageAsset(source) || isVideoAsset(source) ? ['visual_asset'] : ['source_material']
}

export const AddContext = ({
  sources,
  setSources,
  isUploading,
  setIsUploading,
  acceptMedia = true,
  onAnalysisPendingChange,
  brandKitId,
  brandKitMediaEnabled,
}: AddContextProps) => {
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectQuery = useUploadsProject(workspaceId)
  const { openModal } = useModals()
  const { mutateAsync: createMediaObjects } = useCreateMediaObjectsWithProgress({ onProgress: () => {} })
  const { mutateAsync: createSource } = useCreateSource()

  // Track source IDs whose analyses are still in-flight.
  const [pendingAnalysisIds, setPendingAnalysisIds] = useState<Set<number | string>>(new Set())
  const prevSourcesRef = useRef<CampaignSource[]>(sources)

  // When new source_material-role sources are added, mark them as pending.
  // When sources are removed, clear them from the pending set.
  useEffect(() => {
    const prevIds = new Set<number | string>(prevSourcesRef.current.map((cs) => cs.source.id))
    const currentIds = new Set<number | string>(sources.map((cs) => cs.source.id))

    setPendingAnalysisIds((prev) => {
      const next = new Set(prev)
      // Add newly added source_material sources
      sources
        .filter((cs) => !prevIds.has(cs.source.id) && cs.roles.includes('source_material'))
        .forEach((cs) => next.add(cs.source.id))
      // Remove sources that were deleted from the list
      ;[...next].filter((id) => !currentIds.has(id)).forEach((id) => next.delete(id))
      return next
    })

    prevSourcesRef.current = sources
  }, [sources])

  // Notify parent whenever pending status changes.
  useEffect(() => {
    onAnalysisPendingChange?.(pendingAnalysisIds.size > 0)
  }, [pendingAnalysisIds, onAnalysisPendingChange])

  // A source is no longer pending once its analysis settles — whether it produced
  // an analysis or turned out to be unreadable.
  const handleAnalysisSettled = useCallback((sourceId: number | string) => {
    setPendingAnalysisIds((prev) => {
      const next = new Set(prev)
      next.delete(sourceId)
      return next
    })
  }, [])
  const uploadsProjectKey = uploadsProjectQuery.data?.project?.id ?? null

  const addSource = useCallback(
    async (source: CampaignSourceObject) => {
      const roles = defaultRolesForSource(source)
      let mediaFileId: number | undefined

      if (isVisualAsset(source) && uploadsProjectKey) {
        const media = source as SourceMaterialsMediaObject
        const res = await MediaFileClient.create(workspaceId, {
          source_url: media.source_url,
          mime_type: media.mime_type,
          title: media.source_url.split('/').pop() ?? 'upload',
          project_key: uploadsProjectKey,
        })
        if (res.type === SUCCESS_RESPONSE_TYPE) {
          const { almanac_file } = res.data as { almanac_file: AlmanacFile }
          mediaFileId = almanac_file.media_file_id
        } else {
          showToastMessage('Could not register as visual asset', 'error', undefined, 5)
        }
      }

      setSources((prev) => [...prev, { source, roles, mediaFileId }])
    },
    [workspaceId, uploadsProjectKey, setSources],
  )

  const removeSource = useCallback(
    (id: number | string) => {
      setSources((prev) => prev.filter((cs) => cs.source.id !== id))
    },
    [setSources],
  )

  // Used by BrandKitContextModal — skips the media-file creation step because
  // brand kit media files already have a media_file_id.
  const addSourceDirect = useCallback(
    (source: CampaignSourceObject, mediaFileId?: number) => {
      const roles = defaultRolesForSource(source)
      setSources((prev) => {
        if (prev.some((cs) => cs.source.id === source.id)) return prev
        return [...prev, { source, roles, mediaFileId }]
      })
    },
    [setSources],
  )

  const openBrandKitModal = useCallback(() => {
    if (!brandKitId) return

    openModal(
      BrandKitContextModal,
      { brandKitId, existingSources: sources, onAdd: addSourceDirect, mediaEnabled: brandKitMediaEnabled },
      { newStack: true },
    )
  }, [openModal, brandKitId, sources, addSourceDirect, brandKitMediaEnabled])

  const handleFiles = useCallback(
    async (files: File[]) => {
      setIsUploading(true)
      const dismiss = showToastMessage(`Uploading ${pluralize('file', files.length)}...`, 'loading', undefined, 0)
      try {
        const mediaObjects = await createMediaObjects(Array.from(files).map((file) => ({ file, filename: file.name })))
        await Promise.all(
          mediaObjects.map(async (mediaObject) => {
            const result = await createSource({ media_object_id: mediaObject.id, type: 'SourceMaterialsMediaObject' })
            addSource(result.source)
          }),
        )
        dismiss()
        if (mediaObjects.length > 0) {
          showToastMessage(`${pluralize('File', files.length)} uploaded`, 'success', undefined, 3)
        } else {
          showToastMessage('Only documents are supported', 'error', undefined, 3)
        }
      } catch {
        dismiss()
        showToastMessage(
          `There was a problem uploading your ${pluralize('file', files.length)}. Try again.`,
          'error',
          undefined,
          5,
        )
      } finally {
        setIsUploading(false)
      }
    },
    [createMediaObjects, createSource, addSource, setIsUploading],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFiles,
    noClick: true,
    noKeyboard: true,
    accept: acceptMedia ? ACCEPT_WITH_MEDIA : DEFAULT_ACCEPT,
  })

  return (
    <div className={Styles.body}>
      {sources.length === 0 ? (
        <div
          className={cx(Styles.emptyState, { [Styles.dragActive]: isDragActive })}
          {...getRootProps()}
          aria-label="File drop zone"
        >
          <input {...getInputProps()} />
          <div className={Styles.emptyIcon}>
            <Plus size={20} color="var(--dark-40)" />
          </div>
          {isDragActive && <div className={Styles.dragOverlay}>Drop your files here</div>}
          {!isDragActive && (
            <>
              <div className={Styles.emptyText}>
                <Text variant="secondary" color="var(--dark-60)">
                  No context added yet.
                </Text>
                <Text variant="secondary" color="var(--dark-60)">
                  Add a website or upload files to get started.
                </Text>
              </div>
              <div className={Styles.actions}>
                <AddWebpageButton
                  size="lg"
                  isDisabled={isUploading}
                  onSourceCreated={addSource}
                  primaryAction="Add Website"
                  allowSaveMedia={false}
                />
                <UploadButton
                  size="lg"
                  variant="secondary"
                  acceptMedia={acceptMedia}
                  useToastLoading
                  isDisabled={isUploading}
                  onSourceCreated={addSource}
                  onUploadStart={() => setIsUploading(true)}
                  onUploadEnd={() => setIsUploading(false)}
                />
                {brandKitId != null && (
                  <Button
                    variant="secondary"
                    size="lg"
                    frontIcon={Brand}
                    isDisabled={isUploading}
                    onPress={openBrandKitModal}
                  >
                    Select from Brand Kit
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className={Styles.sourceList}>
            {sources.map((cs) => (
              <SourceCard
                key={cs.source.id}
                campaignSource={cs}
                isUploading={isUploading}
                onRemove={removeSource}
                onAnalysisSettled={handleAnalysisSettled}
              />
            ))}
          </div>
          <div className={Styles.actions}>
            <AddWebpageButton
              size="lg"
              isDisabled={isUploading}
              onSourceCreated={addSource}
              primaryAction="Add Website"
            />
            <UploadButton
              variant="secondary"
              size="lg"
              acceptMedia={acceptMedia}
              useToastLoading
              isDisabled={isUploading}
              onSourceCreated={addSource}
              onUploadStart={() => setIsUploading(true)}
              onUploadEnd={() => setIsUploading(false)}
            />
            {brandKitId != null && (
              <Button
                variant="secondary"
                size="lg"
                frontIcon={Brand}
                isDisabled={isUploading}
                onPress={openBrandKitModal}
              >
                Select from Brand Kit
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
